import {SessionState} from '@github-ui/agent-sessions/types/session'
import {testIdProps} from '@github-ui/test-id-props'
import {AnchoredOverlay, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {type FocusEvent, memo, useCallback, useRef, useState} from 'react'

import styles from './agent-task-popover.module.css'

// Delay before showing the popover to prevent rapid tooltip flashing when hovering across rows
const POPOVER_SHOW_DELAY_MS = 50
// Delay before hiding the popover to allow mouse movement from anchor to overlay content
const POPOVER_HIDE_DELAY_MS = 200

export interface AgentTaskPopoverTaskItem {
  id?: string
  title?: string
  nwo?: string
  state: SessionState
  createdAt?: string
  lastUpdatedAt?: string
  href?: string
}

export interface AgentTaskPopoverProps {
  /** Array of tasks to display in the popover. */
  tasks: Array<AgentTaskPopoverTaskItem>
  children: React.ReactNode
  /** Optional className for the popover anchor wrapper div */
  className?: string
}

function TaskItemContent({
  title,
  nwo,
  state,
  lastUpdatedAt,
}: {
  title?: string
  nwo?: string
  state: SessionState
  lastUpdatedAt?: string
}) {
  const isQueued = state === SessionState.Queued
  const taskLastUpdatedAt = lastUpdatedAt
  return (
    <>
      <div className={styles.header}>
        {title && <span className={styles.title}>{title}</span>}
        {!isQueued && taskLastUpdatedAt && (
          <span className={styles.elapsedTime}>
            Updated <RelativeTime date={new Date(taskLastUpdatedAt)} />
          </span>
        )}
      </div>
      {nwo && <div className={styles.subtitle}>{nwo}</div>}
    </>
  )
}

function TaskListContent({
  tasks,
  showPopover,
  hidePopover,
}: {
  tasks: Array<AgentTaskPopoverTaskItem>
  showPopover: () => void
  hidePopover: () => void
}) {
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null
      if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
        hidePopover()
      }
    },
    [hidePopover],
  )

  return (
    <div
      className={styles.taskList}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onFocus={showPopover}
      onBlur={handleBlur}
      {...testIdProps('agent-task-popover')}
    >
      {tasks.map((task, index) => {
        const content = (
          <TaskItemContent title={task.title} nwo={task.nwo} state={task.state} lastUpdatedAt={task.lastUpdatedAt} />
        )
        const key = task.id ?? task.href ?? index
        if (task.href) {
          return (
            <a key={key} href={task.href} target="_blank" rel="noreferrer" className={styles.taskItem}>
              {content}
            </a>
          )
        }
        return (
          <div key={key} className={styles.taskItem}>
            {content}
          </div>
        )
      })}
    </div>
  )
}

export const AgentTaskPopover = memo(function AgentTaskPopover({tasks, children, className}: AgentTaskPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showPopover = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) return
    showTimeoutRef.current = setTimeout(() => {
      showTimeoutRef.current = null
      setIsOpen(true)
    }, POPOVER_SHOW_DELAY_MS)
  }, [])

  const showPopoverImmediate = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    setIsOpen(true)
  }, [])

  const hidePopover = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, POPOVER_HIDE_DELAY_MS)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  if (!tasks || tasks.length === 0) {
    return <>{children}</>
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={clsx(styles.popoverWrapper, className)}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocusCapture={showPopoverImmediate}
        onBlurCapture={hidePopover}
        {...testIdProps('agent-task-popover-wrapper')}
      >
        {children}
      </div>
      <AnchoredOverlay
        open={isOpen}
        renderAnchor={null}
        anchorRef={anchorRef}
        onClose={handleClose}
        focusTrapSettings={{disabled: true}}
        side="outside-top"
        align="center"
        overlayProps={{
          preventFocusOnOpen: true,
          role: 'dialog',
          'aria-label': 'Agent tasks',
        }}
      >
        <TaskListContent tasks={tasks} showPopover={showPopover} hidePopover={hidePopover} />
      </AnchoredOverlay>
    </>
  )
})
